























































































































































import {
  defineComponent,
  SetupContext,
  computed,
  ref,
  reactive,
  watch,
  onMounted,
} from '@vue/composition-api'

import { IReceipt, IItem } from '@/types/receipt'
import { IRecommend } from '@/types/recommend'
import { ReceiptValidation } from '@/types/ocr'
import { getRecommends } from '@/api/recommend'

import { AppInput, AppIcon, MasterAutocomplete } from '@/components/atoms'
import moment from 'moment'

type Props = {
  receipt: IReceipt
  validation: ReceiptValidation
}

export default defineComponent({
  name: 'DetailTable',
  components: { AppInput, AppIcon, MasterAutocomplete },
  props: {
    receipt: {
      type: Object,
      default: () => ({}),
    },
    validation: {
      type: Object,
      default: () => ({}),
    },
  },
  filters: {
    omittedText(text: string): string {
      return text.length > 10 ? text.slice(0, 10) + '…' : text
    },
  },
  setup(props: Props, context: SetupContext) {
    const state = reactive<{
      showCategories: boolean[]
      categoryIds: number[]
      recommendLists: IRecommend[][]
      recommendInputs: string[]
      isSearching: boolean
    }>({
      showCategories: props.receipt.items.map(_ => false),
      categoryIds: props.receipt.items.map(_ => -1),
      recommendLists: props.receipt.items.map(item => {
        return item.matchedCode && item.matchedName
          ? [
              {
                id: '',
                name: item.matchedName,
                code: item.matchedCode,
                price: item.price || 0,
                category: item.category || '',
                isMatched: item.isMatched,
                isToBeDeleted: false,
                _score: 0,
                pbChainStoreIds: null,
              },
            ]
          : []
      }),
      recommendInputs: props.receipt.items.map(item => {
        return item.matchedName ? item.matchedName : ''
      }),
      isSearching: false,
    })

    onMounted(() => {
      const newItems: IItem[] = props.receipt.items.map(item => {
        return {
          ...item,
          isFinallyAutoMatched: item.autoMatchedAt !== null,
          originalMatchedCode: item.matchedCode,
        }
      })
      context.emit('update-items', newItems)
    })

    watch(
      () => props.receipt.id,
      () => {
        state.showCategories = props.receipt.items.map(_ => false)
        state.categoryIds = props.receipt.items.map(_ => -1)
        state.recommendLists = props.receipt.items.map(item => {
          if (item.matchedCode && item.matchedName) {
            const additional = {
              id: '',
              name: item.matchedName,
              code: item.matchedCode,
              price: item.price || 0,
              category: item.category || '',
              isMatched: item.isMatched,
              isToBeDeleted: false,
              _score: 0,
              pbChainStoreIds: null,
            }
            return [additional]
          }
          return []
        })
        state.recommendInputs = props.receipt.items.map(item => {
          return item.matchedName ? item.matchedName : ''
        })
      },
    )

    const appAutocomplete = ref<any>([])

    const isRestaurant = computed<boolean>(() => {
      if (props.receipt.channel === null) {
        return false
      }
      return !!props.receipt.channel.restaurant_flag
    })

    const addItem = (itemIndex: number) => {
      context.emit('on-click-add-item', itemIndex)
      state.showCategories.splice(itemIndex, 0, false)
      state.categoryIds.splice(itemIndex, 0, -1)
      state.recommendLists.splice(itemIndex, 0, [])
      state.recommendInputs.splice(itemIndex, 0, '')
    }
    const deleteItem = (itemIndex: number) => {
      context.emit('on-click-delete-item', itemIndex)
      state.showCategories.splice(itemIndex, 1)
      state.categoryIds.splice(itemIndex, 1)
      state.recommendLists.splice(itemIndex, 1)
      state.recommendInputs.splice(itemIndex, 1)
    }

    const execRecommendSearch = async (q: string, index: number) => {
      if (state.isSearching === true) {
        return
      }
      const categoryId = isRestaurant.value ? 2 : state.categoryIds[index]
      // reactive
      const rcs = [...state.recommendLists]
      try {
        state.isSearching = true
        const rc = await getRecommends(q, categoryId)

        rcs[index] = rc.filter(
          r =>
            r.pbChainStoreIds === null ||
            r.pbChainStoreIds.includes(props.receipt?.chainStoreId ?? 0),
        )
        state.isSearching = false
        state.recommendLists = rcs
      } catch (_) {
        state.isSearching = false
      }
    }

    const clearRecommendList = async (index: number) => {
      const rcs = [...state.recommendLists]
      rcs[index] = []
      state.recommendLists = rcs
    }

    // Update items
    const updateItems = async (key: string, value: any, index: number) => {
      const newItems = props.receipt.items.map((item: IItem, i) => {
        if (index === i) {
          if (['price', 'quantity'].indexOf(key) > -1) {
            value = Number(value)
            const isChanged = (item as any)[key] !== value
            return {
              ...item,
              isChanged,
              [key]: value === 0 && key === 'quantity' ? 1 : value,
            }
          }
          return {
            ...item,
            [key]: value,
          }
        }
        return item
      })

      context.emit('update-items', newItems)
    }

    const nameSearch = async (value: string, index: number) => {
      await execRecommendSearch(value, index)
    }

    const onFocusMasterSelector = (index: number) => {
      state.showCategories = state.showCategories.map((_, i) => {
        return i === index
      })
    }

    const onBlurMasterSelector = () => {
      state.showCategories = state.showCategories.map(_ => {
        return false
      })
    }

    const onSelectRecommend = async (matched: IRecommend, index: number) => {
      const newItems: IItem[] = props.receipt.items.map((item: IItem, i) => {
        if (index === i) {
          if (item.originalMatchedCode !== matched.code) {
            item.reMatchedAt = moment().toDate()
            item.isChanged = true
          } else {
            item.isChanged = false
          }
          return {
            ...item,
            matchedCode: matched.code,
            matchedName: matched.name,
            category: matched.category,
            isMatched: matched.isMatched,
          }
        }
        return item
      })
      context.emit('update-items', newItems)
      onBlurMasterSelector()
    }

    const onUnselectRecommend = async (index: number) => {
      const newItems: IItem[] = props.receipt.items.map((item: IItem, i) => {
        if (index === i) {
          return {
            ...item,
            matchedCode: null,
            matchedName: null,
          }
        }
        return item
      })
      context.emit('update-items', newItems)
    }

    const onClickCategory = async (
      categoryId: number,
      value: string,
      index: number,
    ) => {
      if (state.categoryIds[index] === categoryId) {
        state.categoryIds[index] = -1
      } else {
        state.categoryIds[index] = categoryId
      }
      await execRecommendSearch(value, index)
      appAutocomplete.value[index].$refs.autocomplete.focus()
    }

    // Temporarily save // @todo
    const saving = ref(false)
    const snackbar = ref(false)
    const onClickTemporarilySave = async () => {
      saving.value = true
      setTimeout(() => {
        saving.value = false
        snackbar.value = true
      }, 3000)
    }

    const validatePrice = (e: any) => {
      if (e.isComposing) {
        return true
      }
      const negativeSym = e.target.value.match(/^[-ー－]/) ? '-' : ''
      e.target.value = `${negativeSym}${e.target.value
        .replace(/[０-９]/g, (s: string) => {
          return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
        })
        .replace(/\D/g, '')}`
    }

    const validateQuantity = (e: any) => {
      if (e.isComposing) {
        return true
      }
      if (e.target.value === '') {
        return false
      }
      const negativeSym = e.target.value.match(/^[-ー－]/) ? '-' : ''
      e.target.value = `${negativeSym}${e.target.value
        .replace(/[０-９]/g, (s: string) => {
          return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
        })
        .replace(/\D/g, '')}`.replace(/^0+$/, '1')
    }

    return {
      state,
      appAutocomplete,
      isRestaurant,
      addItem,
      deleteItem,
      updateItems,
      nameSearch,
      saving,
      onClickTemporarilySave,
      snackbar,
      onSelectRecommend,
      onUnselectRecommend,
      onFocusMasterSelector,
      onBlurMasterSelector,
      execRecommendSearch,
      clearRecommendList,
      onClickCategory,
      validatePrice,
      validateQuantity,
    }
  },
})
